import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Briefcase, Copy, CheckCircle2, Server, UserCheck, Zap, Code2, ShieldAlert, RefreshCw, Terminal, Link, Save, ArrowRightLeft, Database } from "lucide-react";
import { REACT_APP_API_URL } from "config/api-config";

export default function ERPSetup({ onClose }) {
  const [activeTab, setActiveTab] = useState("inbound");

  // Original State (Inbound)
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);
  
  const [apiKey, setApiKey] = useState("Loading...");
  const [allowedIps, setAllowedIps] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // New State (Outbound)
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookAuthToken, setWebhookAuthToken] = useState("");
  const [autoSyncOutbound, setAutoSyncOutbound] = useState(false);
  const [savingOutbound, setSavingOutbound] = useState(false);

  const apiUrl = `${REACT_APP_API_URL}/students/erp/sync_customer`;

  // Fetch settings on load
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const res = await axios.get(`${REACT_APP_API_URL}/students/erp/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setApiKey(res.data.api_key);
        setAllowedIps(res.data.allowed_ips || "");
        setWebhookUrl(res.data.webhook_url || "");
        setWebhookAuthToken(res.data.webhook_auth_token || "");
        setAutoSyncOutbound(res.data.auto_sync_outbound || false);
      } catch (error) {
        toast.error("Failed to load ERP settings.");
      }
    };
    fetchSettings();
  }, []);

  // Original Inbound Save
  const handleSaveSecurity = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(`${REACT_APP_API_URL}/students/erp/settings`, 
        { allowed_ips: allowedIps }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Security settings saved!");
    } catch (error) {
      toast.error("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  // New Outbound Save
  const handleSaveOutbound = async () => {
    setSavingOutbound(true);
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(`${REACT_APP_API_URL}/students/erp/settings`, 
        { 
          webhook_url: webhookUrl,
          webhook_auth_token: webhookAuthToken,
          auto_sync_outbound: autoSyncOutbound
        }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Outbound Webhook settings saved!");
    } catch (error) {
      toast.error("Failed to save webhook settings.");
    } finally {
      setSavingOutbound(false);
    }
  };

  // Original Reset Key
  const handleResetKey = async () => {
    if(!window.confirm("Are you sure? This will instantly break your current ERP integration until you update your software with the new key.")) {
      return;
    }
    setResetting(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${REACT_APP_API_URL}/students/erp/settings/reset_key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKey(res.data.api_key); 
      toast.success("Old key destroyed! New key generated.");
    } catch (error) {
      toast.error("Failed to reset API key.");
    } finally {
      setResetting(false);
    }
  };

  const jsonPayload = `{
  "client_id": "POS-9001",
  "customer_name": "Kunden",
  "surname": "Solanki",
  "email_address": "kunden@example.com",
  "phone_number": "789654147",
  "sex": "Male",
  "tier": "Discount",
  "subscription": "10-20k",
  "currency_code": "INR"
}`;

  const curlCommand = `curl -X POST ${apiUrl} \\
-H "Content-Type: application/json" \\
-H "x-api-key: ${apiKey}" \\
-d '{"client_id": "POS-9001", "customer_name": "Kunden", "surname": "Solanki", "email_address": "kunden@example.com", "phone_number": "789654147", "sex": "Male", "tier": "Discount", "subscription": "10-20k", "currency_code": "INR"}'`;

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  return (
    <div style={{ backgroundColor: "#FFFFFF", padding: "30px", borderRadius: "20px", boxShadow: "0px 4px 25px rgba(0,0,0,0.05)", maxHeight: "90vh", overflowY: "auto" }}>
      
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "25px", borderBottom: "1px solid #F4F7FE", paddingBottom: "20px" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "14px", backgroundColor: "#fff4eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Briefcase size={28} color="#FF912C" />
        </div>
        <div>
          <h3 style={{ margin: 0, color: "#2B3674", fontSize: "20px", fontWeight: "700" }}>ERP / POS Integration</h3>
          <p style={{ margin: 0, color: "#A3AED0", fontSize: "14px" }}>Automate your customer entries across platforms.</p>
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: "flex", borderBottom: "1px solid #E2E8F0", marginBottom: "25px", gap: "10px" }}>
        <button 
          onClick={() => setActiveTab("inbound")}
          style={{ padding: "12px 16px", background: "none", border: "none", borderBottom: activeTab === "inbound" ? "3px solid #FF912C" : "3px solid transparent", color: activeTab === "inbound" ? "#FF912C" : "#A3AED0", fontWeight: "700", fontSize: "15px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "0.2s" }}
        >
          <Database size={18} /> Push Data TO CamSec (API)
        </button>
        <button 
          onClick={() => setActiveTab("outbound")}
          style={{ padding: "12px 16px", background: "none", border: "none", borderBottom: activeTab === "outbound" ? "3px solid #FF912C" : "3px solid transparent", color: activeTab === "outbound" ? "#FF912C" : "#A3AED0", fontWeight: "700", fontSize: "15px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "0.2s" }}
        >
          <ArrowRightLeft size={18} /> Pull Data FROM CamSec (Webhook)
        </button>
      </div>

      {/* ========================================= */}
      {/* TAB 1: ORIGINAL INBOUND FLOW              */}
      {/* ========================================= */}
      {activeTab === "inbound" && (
        <div style={{ animation: "fadeIn 0.3s ease-out" }}>
          {/* HUMAN EXPLANATION */}
          <div style={{ marginBottom: "25px", backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", borderLeft: "4px solid #4318FF" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>What does this do?</h4>
            <p style={{ margin: "0", color: "#475467", fontSize: "14px", lineHeight: "1.6" }}>
              Normally, when a new customer registers, you have to type their details into your billing software <b>and</b> into CamSec Ai. 
              <br/><br/>
              By giving the details below to your IT provider, the systems will "talk" to each other. Whenever you create a customer in your POS, they instantly appear in CamSec Ai—no double data entry required!
            </p>
          </div>

          {/* HOW IT WORKS STEPS */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div style={{ minWidth: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#e6f5ff", color: "#0088cc", display: "flex", alignItems: "center", justifyContent: "center" }}><Server size={20}/></div>
              <div>
                <h5 style={{ margin: "0 0 2px 0", color: "#2B3674", fontSize: "14px", fontWeight: "700" }}>1. Enter Customer</h5>
                <p style={{ margin: 0, color: "#A3AED0", fontSize: "13px" }}>Register a user in your native ERP/POS system.</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div style={{ minWidth: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#e9fced", color: "#25D366", display: "flex", alignItems: "center", justifyContent: "center" }}><Zap size={20}/></div>
              <div>
                <h5 style={{ margin: "0 0 2px 0", color: "#2B3674", fontSize: "14px", fontWeight: "700" }}>2. Auto-Sync</h5>
                <p style={{ margin: 0, color: "#A3AED0", fontSize: "13px" }}>Your software pushes the data to CamSec Ai via Webhook.</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div style={{ minWidth: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#f0ebff", color: "#4318FF", display: "flex", alignItems: "center", justifyContent: "center" }}><UserCheck size={20}/></div>
              <div>
                <h5 style={{ margin: "0 0 2px 0", color: "#2B3674", fontSize: "14px", fontWeight: "700" }}>3. Ready for Cameras</h5>
                <p style={{ margin: 0, color: "#A3AED0", fontSize: "13px" }}>The customer is added to the database. Scan their face next time they visit.</p>
              </div>
            </div>
          </div>

          {/* SECURITY CONFIGURATION */}
          <div style={{ borderTop: "1px dashed #E2E8F0", paddingTop: "25px", marginBottom: "20px" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
              <ShieldAlert size={18} color="#EE5D50" /> Security Configuration
            </h4>
            <p style={{ margin: "0 0 15px 0", color: "#A3AED0", fontSize: "13px", lineHeight: "1.5" }}>
              Only servers with the secret API Key can send data to your database. For extreme security, restrict access to specific IT Server IP addresses.
            </p>

            {/* API KEY WITH RESET BUTTON */}
            <div style={{ marginBottom: "15px" }}>
              <p style={{ margin: "0 0 6px 0", fontSize: "12px", color: "#2B3674", fontWeight: "700" }}>YOUR SECRET API KEY</p>
              <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                <input 
                  type="text" 
                  readOnly 
                  value={apiKey} 
                  style={{ flexGrow: 1, minWidth: "250px", padding: "10px", borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "14px", backgroundColor: "#F8FAFC", color: "#EE5D50", fontWeight: "600", letterSpacing: "1px" }} 
                />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={() => copyToClipboard(apiKey, setCopiedKey)} style={{ padding: "10px 16px", backgroundColor: "#F4F7FE", color: "#4318FF", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}>
                    {copiedKey ? "Copied" : "Copy"}
                  </button>
                  
                  <button 
                    onClick={handleResetKey} 
                    disabled={resetting}
                    title="Generate a new key and destroy the old one"
                    style={{ padding: "10px 16px", backgroundColor: "#ffeeec", color: "#EE5D50", border: "none", borderRadius: "8px", cursor: resetting ? "not-allowed" : "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}
                  >
                    <RefreshCw size={16} className={resetting ? "animate-spin" : ""} />
                    {resetting ? "Resetting..." : "Reset Key"}
                  </button>
                </div>
              </div>
            </div>

            {/* ALLOWED IPS */}
            <div style={{ marginBottom: "15px" }}>
              <p style={{ margin: "0 0 6px 0", fontSize: "12px", color: "#2B3674", fontWeight: "700" }}>WHITELISTED IP ADDRESSES (OPTIONAL)</p>
              <input 
                type="text" 
                placeholder="e.g. 203.0.113.45, 198.51.100.12 (Leave blank to allow all IPs)" 
                value={allowedIps}
                onChange={(e) => setAllowedIps(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "14px", outline: "none", color: "#2B3674" }} 
              />
            </div>
            <button onClick={handleSaveSecurity} disabled={saving} style={{ padding: "8px 16px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
              {saving ? "Saving..." : "Save Security Rules"}
            </button>
          </div>

          {/* DEVELOPER SECTION */}
          <div style={{ borderTop: "1px dashed #E2E8F0", paddingTop: "25px", marginBottom: "20px" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>For Your IT Developer:</h4>
            <p style={{ margin: "0 0 15px 0", color: "#A3AED0", fontSize: "13px", lineHeight: "1.5" }}>
              Send a <b>POST</b> request to the Webhook URL below. You must include the header: <code>x-api-key: YOUR_SECRET_KEY</code>.
            </p>

            {/* URL BLOCK */}
            <div style={{ backgroundColor: "#F8FAFC", padding: "16px", borderRadius: "12px", border: "1px solid #E2E8F0", marginBottom: "20px" }}>
              <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#A3AED0", fontWeight: "700" }}>WEBHOOK ENDPOINT URL</p>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <code style={{ flexGrow: 1, color: "#4318FF", fontSize: "14px", fontWeight: "600", wordBreak: "break-all" }}>{apiUrl}</code>
                <button onClick={() => copyToClipboard(apiUrl, setCopiedUrl)} style={{ padding: "8px 12px", backgroundColor: "#F4F7FE", color: "#4318FF", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "4px" }}>
                  {copiedUrl ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>

            {/* cURL COMMAND BLOCK */}
            <div style={{ backgroundColor: "#0B1437", padding: "16px", borderRadius: "12px", marginBottom: "20px", border: "1px solid #2B3674" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "#A3AED0", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
                  <Terminal size={14} /> TEST IT NOW (cURL)
                </p>
                <button onClick={() => copyToClipboard(curlCommand, setCopiedCurl)} style={{ background: "none", border: "none", color: "#00E396", cursor: "pointer", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                  {copiedCurl ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                  {copiedCurl ? "COPIED" : "COPY CMD"}
                </button>
              </div>
              <p style={{ margin: "0 0 10px 0", color: "#8F9BBA", fontSize: "12px" }}>Copy and paste this into your terminal to instantly test the connection. The JSON payload is formatted on a single line to prevent terminal pasting errors.</p>
              <pre style={{ margin: 0, padding: "12px", backgroundColor: "#111C44", color: "#00E396", borderRadius: "8px", fontSize: "13px", fontFamily: "monospace", overflowX: "auto" }}>
                {curlCommand}
              </pre>
            </div>

            {/* JSON PAYLOAD BLOCK */}
            <div style={{ backgroundColor: "#111C44", padding: "16px", borderRadius: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "#A3AED0", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
                  <Code2 size={14} /> EXAMPLE JSON FORMAT
                </p>
                <button onClick={() => copyToClipboard(jsonPayload, setCopiedJson)} style={{ background: "none", border: "none", color: "#00E396", cursor: "pointer", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                  {copiedJson ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                  {copiedJson ? "COPIED" : "COPY JSON"}
                </button>
              </div>
              <pre style={{ margin: 0, padding: "15px", backgroundColor: "#0B1437", color: "#00E396", borderRadius: "8px", fontSize: "13px", fontFamily: "monospace", overflowX: "auto", border: "1px solid #2B3674" }}>
                {jsonPayload}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/* TAB 2: NEW OUTBOUND FLOW                  */}
      {/* ========================================= */}
      {activeTab === "outbound" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px", animation: "fadeIn 0.3s ease-out" }}>
          
          <div style={{ backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", borderLeft: "4px solid #FF912C" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>How Outbound Sync Works</h4>
            <p style={{ margin: "0", color: "#475467", fontSize: "14px", lineHeight: "1.6" }}>
              Instead of your ERP pushing data to us, CamSec Ai will act as the sender. Whenever a customer is registered in CamSec Ai, we will automatically HTTP POST their JSON details to your external Webhook URL.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
              <Link size={16} color="#4318FF"/> Your Destination Webhook URL
            </label>
            <input 
              type="text" 
              placeholder="https://your-company.com/api/customer/webhook"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", boxSizing: "border-box", color: "#2B3674", backgroundColor: "#F8FAFC" }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
              <ShieldAlert size={16} color="#4318FF"/> Authentication Token (Optional)
            </label>
            <input 
              type="password" 
              placeholder="e.g. Bearer Token or Secret Key"
              value={webhookAuthToken}
              onChange={(e) => setWebhookAuthToken(e.target.value)}
              style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", boxSizing: "border-box", color: "#2B3674", backgroundColor: "#F8FAFC" }}
            />
          </div>

          <div style={{ backgroundColor: "#FFFFFF", padding: "16px", borderRadius: "12px", border: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0px 2px 10px rgba(0,0,0,0.02)" }}>
            <div>
              <h4 style={{ margin: "0 0 4px 0", color: "#2B3674", fontSize: "15px", fontWeight: "700" }}>Enable Outbound Sync</h4>
              <p style={{ margin: 0, color: "#A3AED0", fontSize: "13px" }}>Automatically push new CamSec registrations to your ERP.</p>
            </div>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", position: "relative" }}>
              <input type="checkbox" style={{ opacity: 0, position: "absolute", width: 0, height: 0 }} checked={autoSyncOutbound} onChange={(e) => setAutoSyncOutbound(e.target.checked)} />
              <div style={{ width: "48px", height: "26px", backgroundColor: autoSyncOutbound ? "#FF912C" : "#E2E8F0", borderRadius: "20px", position: "relative", transition: "0.3s", display: "flex", alignItems: "center" }}>
                <div style={{ width: "20px", height: "20px", backgroundColor: "white", borderRadius: "50%", position: "absolute", left: autoSyncOutbound ? "25px" : "3px", transition: "0.3s", boxShadow: "0px 2px 4px rgba(0,0,0,0.2)" }}></div>
              </div>
            </label>
          </div>

          <button 
            onClick={handleSaveOutbound}
            disabled={savingOutbound}
            style={{ padding: "14px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: savingOutbound ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "0.2s" }}
          >
            <Save size={18} />
            {savingOutbound ? "Saving..." : "Save Outbound Settings"}
          </button>

        </div>
      )}

      {/* FOOTER */}
      <div style={{ display: "flex", justifyContent: "flex-end", borderTop: "1px solid #F4F7FE", paddingTop: "20px", marginTop: "20px" }}>
        <button onClick={onClose} style={{ padding: "12px 24px", backgroundColor: "#F4F7FE", color: "#A3AED0", border: "none", borderRadius: "12px", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>
          Close Panel
        </button>
      </div>
    </div>
  );
}
